import React from 'react';
import { isAxiosError } from 'axios';
import { Formik } from 'formik';
import { number, object, string } from 'yup';
import { Box, MenuItem, Typography } from '@mui/material';
import { CalendarDateService } from '@uplift/services';
import { SupervisionMeetingMethod } from '@uplift/api';

import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { useCollaboratePhysicianAndProviderMatchesQuery, useCollaborativeStatesDetailsQuery, useCreateSupervisionMeetingMutation, userDisplayNameQuery, useStatesQuery } from '@uplift/queries';
import { DateTimePicker, FormModal, Loader, ModalActions, ModalContent, ModalTitle, NumberInput, RadioControl, Select, SubmitButton, TextInput, useAlert } from '@uplift/ui-v2';
import { NoteSection } from '../../notes-page/components/NoteSection';
import { useSuspenseQuery } from '@tanstack/react-query';


const METHOD_VALUES: SupervisionMeetingMethod[] = [ 'video', 'phone', 'in_person' ];

type FormValues = {
  meetingDate: string;
  matchId: string;
  duration: number | '';
  method: SupervisionMeetingMethod;
  summary: string;
};

const validationSchema = object({
  meetingDate: string()
    .required('Date is required')
    .test(
      'not-future',
      'Date/time cannot be in the future',
      value => !value || CalendarDateService.date(value).valueOf() <= CalendarDateService.date().valueOf()
    ),
  duration: number()
    .min(1, 'Duration must be at least 1 minute')
    .required('Duration is required'),
  method: string().oneOf(METHOD_VALUES).required('Select a method'),
  summary: string().required('Summary is required'),
});


export type CreateSupervisionMeetingModalProps = {
  open: boolean;
  onClose: () => void;
  providerId: string;
};


const CollaborativeMatchSelect: React.VFC<{ name: string; label: string; physicianId: string; providerId: string }> = ({ name, label, physicianId, providerId }) => {
  const { data } = useCollaboratePhysicianAndProviderMatchesQuery(physicianId, providerId);
  const { data: states } = useStatesQuery({ suspense: true });
  const { data: statesDetails } = useCollaborativeStatesDetailsQuery();

  return (
    <Select label={label} name={name} style={{ width: 300 }}>
      {data.map(m => {
        const collaborativeDetails = statesDetails.find(s => s.stateId === m.stateId);

        if (!collaborativeDetails?.requiresSupervisionMeeting) return null;

        const state = states.find(s => s.id === m.stateId);

        return (
          <MenuItem key={m.id} value={m.id}>{state.fullName}</MenuItem>
        );
      })}
    </Select>
  );
};


const SuperviseeDetails: React.VFC<{ providerId: string }> = ({ providerId }) => {
  const { data: displayName } = useSuspenseQuery(userDisplayNameQuery(providerId));

  return (
    <NoteSection title={displayName.fullWithAffixes} />
  );
};


const CreateSupervisionMeetingModalContent: React.VFC<{ providerId: string }> = ({ providerId }) => {
  const me = useCurrentuser();
  const l10n = useL10n();
  const timezone = useTimezone();

  return (
    <>
      <Box display="flex" flexDirection="column" gap="24px">
        <React.Suspense fallback={<Loader height="80px" />}>
          <SuperviseeDetails providerId={providerId} />
        </React.Suspense>

        <NoteSection title="Details">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 3, pb: 3 }}>
            <CollaborativeMatchSelect
              label="State"
              name="matchId"
              physicianId={me.id}
              providerId={providerId}
            />
            <DateTimePicker
              name="meetingDate"
              label="Date and Time of Meeting"
              format="MM/DD/YYYY hh:mm A"
              valueFormat="YYYY-MM-DDTHH:mm:ssZ"
              timezone={timezone}
              disableFuture
              style={{ width: 250 }}
            />
            <NumberInput
              name="duration"
              label="Duration (minutes)"
              numberInputProps={{ decimalScale: 0, allowNegative: false, min: 1 }}
              sx={{ width: 225 }}
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Method of Communication</Typography>
              <RadioControl
                name="method"
                values={METHOD_VALUES.map(value => ({
                  value,
                  label: l10n.supervisees.meetingMethods[value],
                }))}
              />
            </Box>
          </Box>
        </NoteSection>

        <NoteSection title="Summary">
          <Box sx={{ px: 3, pb: 3 }}>
            <TextInput
              name="summary"
              label="Meeting Summary"
              multiline
              fullWidth
              rows={4}
              placeholder="Summarize the discussion, including patient treatment and care, needs assessment, and opportunities to improve patient care. Note any follow-up items."
            />
          </Box>
        </NoteSection>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
        Signing and submitting serves as your attestation of this supervision meeting record.
      </Typography>
    </>
  );
};


export const CreateSupervisionMeetingModal: React.VFC<CreateSupervisionMeetingModalProps> = ({ open, onClose, providerId }) => {
  const alert = useAlert();
  const createMeetingMutation = useCreateSupervisionMeetingMutation();

  const handleSubmit = async (values: FormValues) => {
    const meetingDate = CalendarDateService.date(values.meetingDate).toISOString();

    const payload = {
      providerId: providerId,
      matchId: values.matchId,
      meetingDate,
      durationMinutes: Number(values.duration),
      method: values.method,
      summary: values.summary,
    };


    try {
      await createMeetingMutation.mutateAsync(payload);

      alert.success('Supervision meeting submitted');
      onClose();
    } catch (err) {
      if (isAxiosError(err) && err.response?.data?.error) {
        alert.error(err.response.data.error);
        return;
      }

      alert.error('Unable to submit supervision meeting');
    }
  };

  return (
    <Formik
      initialValues={{
        meetingDate: '',
        matchId: '',
        duration: '',
        method: null,
        summary: '',
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      <FormModal open={open} maxWidth="md" fullWidth onClose={onClose}>
        <ModalTitle onClose={onClose}>Supervision Meeting</ModalTitle>
        <ModalContent>
          <React.Suspense fallback={<Loader height="600px" />}>
            <CreateSupervisionMeetingModalContent providerId={providerId} />
          </React.Suspense>
        </ModalContent>
        <ModalActions>
          <SubmitButton color="secondary">Sign and Submit</SubmitButton>
        </ModalActions>
      </FormModal>
    </Formik>
  );
};
